import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DocmergeTemplateSectionConfig } from 'classes-lib';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Namespace, SectionType } from 'enums-lib';
import { ISection } from 'interfaces-lib';
import { Subscription } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { SalesforceMappingStoreService } from '../../../../../services/salesforce/salesforce-mapping-store.service';
import { SectionComponentBase } from '../../../../sections/section-component-base';

@Component({
  selector: 'app-docmerge-template-section',
  standalone: false,
  templateUrl: './docmerge-template-section.component.html',
  styleUrl: './docmerge-template-section.component.scss',
})
export class DocmergeTemplateSectionComponent extends SectionComponentBase implements OnInit, OnChanges, OnDestroy {
  get config(): FormGroup {
    return this.section.get('config') as FormGroup;
  }

  @Input() section: FormGroup;
  @Input() entityType: string;

  dropdownEntityTypes = undefined;
  allowedEntityTypes: string[] | undefined = undefined;
  shouldHideFileTypes = false;
  private subscriptions = new Subscription();
  private nameSpace: Namespace;

  constructor(
    private identityStorage: IdentityStorage,
    private salesforceMappingStore: SalesforceMappingStoreService,
  ) {
    super();
    this.nameSpace = this.identityStorage.get().namespace;
    this.shouldHideFileTypes = [Namespace.salesforce].includes(this.nameSpace);
  }

  public static override buildForm(formGroup: UntypedFormGroup, section?: ISection): void {
    const config = section?.config
      ? DocmergeTemplateSectionConfig.clone(section.config as DocmergeTemplateSectionConfig)
      : new DocmergeTemplateSectionConfig();

    const configGroup = new UntypedFormGroup({
      required: new UntypedFormControl(config.required),
      templateEntityType: new UntypedFormControl(config.templateEntityType, Validators.required),
      editable: new UntypedFormControl(config.editable),
      editRequired: new UntypedFormControl(config.editRequired),
      templateFileTypes: new UntypedFormControl(config.templateFileTypes),
      defaultTemplate: new UntypedFormControl(config.defaultTemplate),
    });

    formGroup.setControl('config', configGroup);
    formGroup.get('required').setValue(false);
    formGroup.get('type').setValue(SectionType.DocMergeTemplateSection);
  }

  ngOnInit() {
    this.subscriptions.add(
      this.config.get('templateEntityType').valueChanges.subscribe((templateEntityType) => {
        this.bullhornCandidateSpecialCase(templateEntityType);
      }),
    );
    this.updateAllowedEntityTypes(this.entityType);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityType'] && !changes['entityType'].firstChange) {
      this.updateAllowedEntityTypes(this.entityType);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // for KAD2-2266 - when the entity type is not candidate, add client corporation to the list of entity types
  private bullhornCandidateSpecialCase(entityType: string) {
    const isBullhorn = this.nameSpace === Namespace.bullhorn;
    const isNotCandidate = entityType !== BullhornEntityTypes.Candidate;
    if (isBullhorn && isNotCandidate) {
      this.dropdownEntityTypes = [BullhornEntityTypes.ClientCorporation, entityType];
    } else {
      this.dropdownEntityTypes = [entityType];
    }
  }

  private updateAllowedEntityTypes(entityType: string): void {
    if (this.nameSpace !== Namespace.salesforce || !entityType) {
      this.allowedEntityTypes = undefined;
      return;
    }

    this.subscriptions.add(
      this.salesforceMappingStore.getEnabledMappings().subscribe((mappings) => {
        const mapping = mappings.find((m) => m.objectName === entityType);
        const relatedObjectNames = (mapping?.relatedObjects ?? []).map((r) => r.objectName);
        this.allowedEntityTypes = [entityType, ...relatedObjectNames];
      })
    );
  }
}
